import { useState } from 'react';
import fm from 'format-message';
import PropTypes from 'prop-types';
import { addressFormFields } from '@activebrands/core-web/components/shipping-address/address-fields';
import addIngridAttributes from '@activebrands/core-web/libs/centra/add-ingrid-attributes';
import localStorage from '@activebrands/core-web/libs/local-storage';
import { RichTextPropType } from '@activebrands/core-web/libs/storyblok/RichText';
import handleVisibilitySettings from '@activebrands/core-web/libs/storyblok/utils/handle-visibility-settings';
import { addBasketInformation, registerBasket } from '@activebrands/core-web/state/basket/actions';
import { useTypedDispatch, useTypedSelector } from '@activebrands/core-web/state/store';
import Form from 'components/Form';
import InfoBoxes from 'components/InfoBoxes';
import Heading from 'components/text/Heading';

const NEWSLETTER_SIGNUP_LOCAL_STORAGE = process.env.REACT_APP_NEWSLETTER_SIGNUP_LOCAL_STORAGE;
const INGRID_SITE_TAG = process.env.REACT_APP_INGRID_SITE_TAG || '';

const CustomerForm = ({
    addressNewsletterText,
    addressSeprateShippingText,
    addressTermsText,
    basketType,
    infoBoxes,
    proceedToCheckoutStep,
    separateBillingAndShippingAddress,
    setSeparateBillingAndShippingAddress,
    ...rest
}) => {
    const [customerFeedback, setCustomerFeedback] = useState({ isLoading: false, error: '' });

    const dispatch = useTypedDispatch();
    const aliasId = useTypedSelector(state => state.application.aliasId);
    const basket = useTypedSelector(state => state.basket);
    const ecommerce = useTypedSelector(state => state.ecommerce);
    const locale = useTypedSelector(state => state.application.locale);

    const isAuthenticated = useTypedSelector(state => state.auth.isAuthenticated);
    const user = useTypedSelector(state => state.auth.user);

    const { address, shippingAddress } = basket;
    const availableInMarket = ecommerce.shipping.availableInMarket?.length > 0 && ecommerce.shipping.availableInMarket;

    // 1. Available markets from ecommerce in Redux
    // 2. All available markets from ecommerce in Redux (this is needed for campaign sites to work)
    const shippingMarketCountryCodes = availableInMarket || ecommerce.shipping.available;

    // 1. Selected country state in checkout
    // 2. Selected shipping market from ecommerce in Redux
    const userCountryCode = address?.country || ecommerce.shipping.selected;

    // Handles seperate delivery fields
    const toggleSeparateShippingAndBilling = event => {
        setSeparateBillingAndShippingAddress(event.target.checked);
    };

    const [customerInformation, setCustomerInformation] = useState({
        // Set default address to the baskets address, since it's prioritized over user.
        address: {
            email: address?.email,
            firstName: address?.firstName,
            lastName: address?.lastName,
            phoneNumber: address?.phone,
            state: address?.state,
            address1: address?.address1,
            address2: address?.address2,
            zipCode: address?.zip,
            city: address?.city,
        },
        shippingAddress: {
            email: shippingAddress?.email,
            firstName: shippingAddress?.firstName,
            lastName: shippingAddress?.lastName,
            phoneNumber: shippingAddress?.phone,
            state: shippingAddress?.state,
            address1: shippingAddress?.address1,
            address2: shippingAddress?.address2,
            zipCode: shippingAddress?.zip,
            city: shippingAddress?.city,
        },
    });

    // Find the country id based on country code
    const getCountryByCountryCode = countryId => ecommerce.countries[countryId];

    // Keep track of the selected country
    const [selectedCountry, setSelectedCountry] = useState(getCountryByCountryCode(userCountryCode));

    // Handles the rules and filters InfoBoxes based on visibility settings.
    const handleInfoBoxesRulesData = countryCode => {
        return handleVisibilitySettings({ items: infoBoxes, countryCode, aliasId });
    };

    // Keep track of current infoBoxData
    const [infoBoxData, setInfoBoxData] = useState(handleInfoBoxesRulesData(selectedCountry?.id || basket.country));

    const handleSelectedCountry = value => {
        const selectedCountryData = getCountryByCountryCode(value);

        setSelectedCountry(selectedCountryData);
        setInfoBoxData(handleInfoBoxesRulesData(selectedCountryData?.id || basket.country));
    };

    // Handle the input fields with data
    const checkboxData = {
        addressNewsletterText,
        addressSeprateShippingText,
        addressTermsText,
        separateBillingAndShippingAddress,
        toggleSeparateShippingAndBilling,
    };

    const addressFields = addressFormFields({
        brandSpecific: {
            $styles: {
                address: {
                    firstName: { width: 'calc(50% - 4px)' },
                    lastName: { width: 'calc(50% - 4px)' },
                },
                shippingAddress: {
                    firstName: { width: 'calc(50% - 4px)' },
                    lastName: { width: 'calc(50% - 4px)' },
                    shippingAddress: { marginTop: '24px' },
                },
            },
            components: {
                InfoBoxes: InfoBoxes,
                Heading: Heading,
            },
            zipCodeBlackList: ['HK', 'AE'],
        },
        checkboxData,
        countries: ecommerce.countries,
        data: customerInformation,
        handleSelectedCountry: value => handleSelectedCountry(value),
        infoBoxData,
        selectedCountry,
        shippingMarkets: shippingMarketCountryCodes,
        user: isAuthenticated ? user : undefined,
        userCountry: getCountryByCountryCode(userCountryCode),
    });

    // Submit the form after all validation has gone through
    const submitForm = async fields => {
        // Group fields based on predetermined prefixes, all fields without these prefixes will be removed
        const expectedPrefixes = ['address_', 'shippingAddress_'];
        const removedFields = {};

        let newBasketData = Object.keys(fields).reduce((basketAddress, fieldName) => {
            // Check if the field has the prefix
            if (expectedPrefixes.some(prefix => fieldName.startsWith(prefix))) {
                // Get prefix and original name of the field
                const prefix = fieldName.split('_')[0];
                const noPrefixName = fieldName.split('_')[1];

                if (!basketAddress[prefix]) {
                    basketAddress[prefix] = {};
                }

                // Add field value to the correct basket data group
                basketAddress[prefix][noPrefixName] = fields[fieldName];
            } else {
                // Save the data of the removed field
                removedFields[fieldName] = fields[fieldName];
            }

            return basketAddress;
        }, {});

        // Add additional fields and Ingrid attributes to basket data
        // It seems to be difficult to let TEAMMIX and TEAM tags override the ACT tag in Ingrid.
        // Therefore we remove the ACT tag if the user has TEAMMIX or TEAM tags.
        const userTags = user?.tags || [];
        const filteredUserTags = basketType === 'INLINE' ? userTags : userTags.filter(tag => tag.name !== 'ACT');

        newBasketData = {
            ...newBasketData,
            additionalFields: {
                ingridAttributes: addIngridAttributes(filteredUserTags, isAuthenticated, INGRID_SITE_TAG, locale),
            },
        };

        // Check if we should sign up the user for newsletters
        if (removedFields.newsletter) {
            localStorage.set(NEWSLETTER_SIGNUP_LOCAL_STORAGE, true);
        } else {
            localStorage.remove(NEWSLETTER_SIGNUP_LOCAL_STORAGE);
        }

        setCustomerFeedback(prevState => ({ ...prevState, isLoading: true }));

        try {
            // await this in order to be able to make the proceedToCheckoutStep not falling behind in data.
            await dispatch(addBasketInformation(newBasketData));

            // Order of the following steps is important
            setCustomerInformation(newBasketData);
            setCustomerFeedback(prevState => ({ ...prevState, isLoading: false }));

            if (newBasketData.address?.email && basket.id) {
                dispatch(registerBasket(basket.id, newBasketData.address.email));
            }

            proceedToCheckoutStep();
        } catch (err) {
            setCustomerFeedback(prevState => ({
                ...prevState,
                isLoading: false,
                error: `${fm('Something went wrong...')} ${fm(
                    'please check your details and try again! If the issue continues, please contact customer service.'
                )}`,
            }));
            setTimeout(() => setCustomerFeedback(prevState => ({ ...prevState, isLoading: false, error: '' })), 6500);
        }
    };

    return (
        <Form
            $style={{
                padding: ['12px var(--margin-content) 64px', null, null, null, null, '12px var(--margin-content) 0'],
            }}
            buttonLabel={customerFeedback.error ? customerFeedback.error : fm('Continue to shipping')}
            buttonProps={{ style: { margin: '24px 0 0', width: ['100%', null, 'auto'] } }}
            fields={addressFields}
            loading={customerFeedback.isLoading}
            onSubmit={submitForm}
            {...rest}
        />
    );
};

CustomerForm.propTypes = {
    addressNewsletterText: RichTextPropType,
    addressSeprateShippingText: RichTextPropType,
    addressTermsText: PropTypes.object,
    basketType: PropTypes.string,
    infoBoxes: PropTypes.array,
    proceedToCheckoutStep: PropTypes.func.isRequired,
    separateBillingAndShippingAddress: PropTypes.bool.isRequired,
    setSeparateBillingAndShippingAddress: PropTypes.func.isRequired,
};

export default CustomerForm;
